import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CaptainLocation, CaptainLocationInput } from '@ridemaadi/types';
import { apiFetch } from '../../lib/api-client';

export const locationApi = {
  current: (rideId: string) => apiFetch<CaptainLocation | null>(`/rides/${rideId}/location`),
  update: (rideId: string, input: CaptainLocationInput) =>
    apiFetch<CaptainLocation>(`/rides/${rideId}/location`, { method: 'POST', body: input }),
  setSharing: (rideId: string, enabled: boolean) =>
    apiFetch<{ enabled: boolean }>(`/rides/${rideId}/sharing`, { method: 'POST', body: { enabled } }),
  realtimeUrl: (rideId: string) => apiFetch<{ url: string }>(`/rides/${rideId}/realtime`),
};

export const locationKey = (rideId: string) => ['rides', 'location', rideId] as const;

export function useCaptainLocation(rideId: string | undefined, refetchMs?: number) {
  return useQuery({
    queryKey: locationKey(rideId ?? ''),
    queryFn: () => locationApi.current(rideId!),
    enabled: Boolean(rideId),
    refetchInterval: refetchMs,
  });
}

export function useUpdateLocation(rideId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CaptainLocationInput) => locationApi.update(rideId, input),
    onSuccess: (data) => qc.setQueryData(locationKey(rideId), data),
  });
}

export function useSetSharing(rideId: string) {
  return useMutation({ mutationFn: (enabled: boolean) => locationApi.setSharing(rideId, enabled) });
}
